import React, {
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  TimeRecord,
  TimeRecordCalculatedData,
  TimeRecordSearchResultItem,
} from '../../../models/TimeRecord';
import { formatMinutesFloat, generateFilterOptions } from '../../../util';
import moment from 'moment';
import useFilterContext from '../../../components/Filter/hooks/useFilterContext';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import FilterResultNoItemsScreen from '../../../components/Filter/FilterResultNoItemsScreen';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import { useExportTimeRecordsToCsv } from '../export';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import { sumUpDurationInMinutes } from '../util';
import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import { makePrioStyles } from '../../../theme/utils';
import { sortContactsHelper } from '../../contacts/utils';
import { Classes } from 'jss';
import { VirtualTableBodyRef } from '@prio365/prio365-react-library';
import useExcelExport from '../../../hooks/useExcelExport';
import useAccessRights2 from '../../users/hooks/useAccessRights2';
import { createSelector } from 'reselect';
import { RootReducerState } from '../../../apps/main/rootReducer';
import { SettingState } from '../../settings/reducers/setting';
import { ESettingScope, Setting } from '../../../models/Settings';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ActiveFiltersType } from '@prio365/prio365-react-library/lib/VirtualTable/types/common';

const useStyles = makePrioStyles((theme) => ({
  tableCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const betaSettingsSelector = createSelector<
  [(state: RootReducerState) => SettingState],
  Setting[]
>(
  (state) => state.settings.setting,
  ({ settings }) => {
    return settings.filter(
      (setting) =>
        setting.settingId !== '00000000-0000-0000-0000-000000000000' &&
        setting.key === 'General_BetaFeaturesEnabled'
    );
  }
);

export interface TimeRecordsPageTableRef {
  fetchTimeRecords: () => void;
  timeRecords: TimeRecordSearchResultItem[];
  resetActiveFilters: () => void;
}

interface TimeRecordsTableProps {
  className?: string;
  tableId: string;
  selectedTimeRecords: TimeRecord[];
  onRowClick?: (entry: TimeRecord) => void;
  onSelectionChange?: (items: TimeRecord[]) => void;
  onDelete?: () => void;
  setTimeRecords?: (value: TimeRecord[]) => void;
  setReportDrawerVisible?: (value: boolean) => void;
  setBetaReportDrawerVisible?: (value: boolean) => void;
  setTimeRecordSearchResultItems?: (
    value: TimeRecordSearchResultItem[]
  ) => void;
  onFilterChange?: (args: {
    appliedFiltersEnabled: boolean;
    data: TimeRecord[];
  }) => void;
}

export const TimeRecordsPageTable = forwardRef(
  (props: TimeRecordsTableProps, ref: React.Ref<TimeRecordsPageTableRef>) => {
    //#region ------------------------------ Defaults

    const {
      className,
      tableId,
      selectedTimeRecords,
      onRowClick,
      onSelectionChange,
      onDelete,
      setTimeRecords,
      setReportDrawerVisible,
      setBetaReportDrawerVisible,
      setTimeRecordSearchResultItems,
      onFilterChange,
    } = props;
    const { t } = useTranslation();

    const classes = useStyles();
    const { projectId } = useParams();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const innerRef = useRef<VirtualTableBodyRef>(null);

    const { getContactById } = useContactsContext();
    const { getProjectById } = useProjectsContext();

    const exportExcel = useExcelExport();

    const { data, isLoading, fetchSearch } = useFilterContext<
      TimeRecord,
      TimeRecordCalculatedData
    >();

    const timeRecords = useMemo(
      () =>
        data?.items?.sort((a, b) => {
          if (a.data.day === b.data.day) {
            const aLastName = getContactById(a.data.contactId)?.lastName;
            const bLastName = getContactById(b.data.contactId)?.lastName;
            if (aLastName === bLastName) {
              const aFirstName = getContactById(a.data.contactId)?.firstName;
              const bFirstName = getContactById(b.data.contactId)?.firstName;
              return (aFirstName ?? '').localeCompare(bFirstName ?? '');
            }
            return (aLastName ?? '').localeCompare(bLastName ?? '');
          }
          return moment(b.data.day).diff(moment(a.data.day));
        }) ?? [],
      [data?.items, getContactById]
    );

    const [filteredTimeRecords, setFilteredTimeRecords] = useState<
      TimeRecordSearchResultItem[] | null
    >(null);

    const _timeRecords: TimeRecord[] = useMemo(() => {
      if (Boolean(filteredTimeRecords)) {
        return filteredTimeRecords.map((tr) => tr.data) ?? [];
      }
      return timeRecords.map((tr) => tr.data) ?? [];
    }, [timeRecords, filteredTimeRecords]);

    const _selectedTimeRecords: TimeRecordSearchResultItem[] = useMemo(
      () =>
        selectedTimeRecords.map((selectedTimeRecord) => {
          return timeRecords.find(
            (timeRecord) =>
              timeRecord.data.timeRecordId === selectedTimeRecord.timeRecordId
          );
        }) ?? [],
      [selectedTimeRecords, timeRecords]
    );

    const getProjectLabelById = useCallback(
      (id: string): string => {
        const project = getProjectById(id);
        return project ? `${project.number} ${project.shortName}` : '';
      },
      [getProjectById]
    );

    const getContactLabelById = useCallback(
      (id: string): string => {
        const contact = getContactById(id);
        return contact ? `${contact.firstName} ${contact.lastName}` : '';
      },
      [getContactById]
    );

    const projectOptions = useMemo(() => {
      return generateFilterOptions<TimeRecordSearchResultItem>(
        timeRecords,
        'data.projectId',
        getProjectLabelById
      );
    }, [timeRecords, getProjectLabelById]);

    const projectPhaseOptions = useMemo(() => {
      return generateFilterOptions<TimeRecordSearchResultItem>(
        timeRecords,
        'calculated.projectPhase'
      );
    }, [timeRecords]);

    const contactOptions = useMemo(() => {
      return generateFilterOptions<TimeRecordSearchResultItem>(
        timeRecords,
        'data.contactId',
        getContactLabelById
      );
    }, [timeRecords, getContactLabelById]);

    const {
      checkGlobalRoles,
      checkOfficeRolesOfProjectId,
      checkProjectRolesOfProjectId,
    } = useAccessRights2();

    const betaSettings = useSelector(betaSettingsSelector);

    const betaUserSettings = betaSettings.filter(
      (setting) => setting.scope === ESettingScope.USER
    );

    const isBetaFeaturesEnabled = useMemo(() => {
      const defaultSetting =
        betaUserSettings.find((setting) => setting.isDefault === true) ||
        betaUserSettings[0] ||
        betaSettings[0];
      const value = JSON.parse(defaultSetting?.jsonValue || '{}');
      return value?.isEnabled ?? false;
    }, [betaSettings, betaUserSettings]);

    const isController = useMemo(() => {
      return (
        checkGlobalRoles(['globalController']) ||
        checkOfficeRolesOfProjectId(['officeController'], projectId) ||
        checkProjectRolesOfProjectId(['projectController'], projectId)
      );
    }, [
      projectId,
      checkGlobalRoles,
      checkOfficeRolesOfProjectId,
      checkProjectRolesOfProjectId,
    ]);

    const isMeArea = window.location.pathname.includes('projects/me/');
    const showCreateReportButton = !isMeArea && isController;
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleOnRowClick: (
      item: TimeRecordSearchResultItem
    ) => VirtualListItemOnRowProps = useCallback(
      (item) => {
        return {
          onClick: (e) => {
            onRowClick(item.data);
          },
        };
      },
      [onRowClick]
    );

    const handleOnSelectionChange: (
      items: TimeRecordSearchResultItem[]
    ) => void = useCallback(
      (items) => {
        onSelectionChange(items.map((item) => item.data));
      },
      [onSelectionChange]
    );

    const exportToCsv = useExportTimeRecordsToCsv();

    const createReport = () => {
      setReportDrawerVisible?.(true);
    };

    const createBetaReport = () => {
      setBetaReportDrawerVisible?.(true);
    };

    const handleOnFilterChange = useCallback(
      (
        activeFilters: ActiveFiltersType,
        data: TimeRecordSearchResultItem[],
        appliedFilters: boolean
      ) => {
        setFilteredTimeRecords(appliedFilters ? data : null);
        if (onFilterChange) {
          onFilterChange({
            appliedFiltersEnabled: appliedFilters,
            data: data.map((item) => item.data),
          });
        }
      },
      [onFilterChange]
    );

    const handleOnExcelExportClick = useCallback(() => {
      const items =
        _selectedTimeRecords.length > 0 ? _selectedTimeRecords : timeRecords;
      const exportItems: Record<string, string | number>[] = items.map(
        (item) => {
          return {
            [t('timeRecords:table.columnTitle.day')]: moment(
              item.data.day
            ).format('DD.MM.YYYY'),
            [t('timeRecords:table.columnTitle.projectName')]:
              getProjectLabelById(item.data.projectId),
            [t('timeRecords:table.columnTitle.title')]: item.data.title,
            [`${t('timeRecords:table.columnTitle.duration')} (h)`]: `${formatMinutesFloat(
              sumUpDurationInMinutes(item.data.timeRecordEntries) ?? 0,
              false
            )}`,
            [`${t('timeRecords:table.columnTitle.kilometerDistance')} (km)`]:
              item.data.kilometerDistance,
            [t('timeRecords:table.columnTitle.projectPhase')]:
              item.calculated.projectPhase || '-',
            [t('timeRecords:table.columnTitle.contactName')]:
              getContactLabelById(item.data.contactId),
            [t('timeRecords:table.columnTitle.invoiceNumber')]:
              item.data.invoiceNumber || '-',
            [t('timeRecords:table.columnTitle.description')]:
              item.data.description || '-',
          };
        }
      );
      exportExcel(exportItems, t('timeRecords:timeRecords'));
    }, [
      t,
      _selectedTimeRecords,
      timeRecords,
      getProjectLabelById,
      getContactLabelById,
      exportExcel,
    ]);

    const actionBarButtons = [
      {
        children: t('timeRecords:table.actionBarActions.deleteSelected'),
        iconProp: 'trash-alt',
        onClick: onDelete,
      },
      ...(showCreateReportButton
        ? [
            {
              children: t('timeRecords:table.actionBarActions.createReport'),
              iconProp: 'file',
              onClick: createReport,
            },
          ]
        : []),
      ...(showCreateReportButton && isBetaFeaturesEnabled
        ? [
            {
              children: `${t('timeRecords:table.actionBarActions.createReport')} (Beta)`,
              iconProp: 'file',
              onClick: createBetaReport,
            },
          ]
        : []),
      {
        children: t('common:actions.excelExportSelection'),
        iconProp: 'file-excel',
        onClick: handleOnExcelExportClick,
      },
      {
        children: t('common:actions.csvExportSelection'),
        iconProp: 'file-arrow-down',
        onClick: () => {
          exportToCsv(_selectedTimeRecords);
        },
      },
    ];
    //#endregion

    //#region ------------------------------ Columns
    const columns: Column<TimeRecordSearchResultItem>[] = useMemo(
      () => [
        {
          id: 'day',
          accessor: 'data.day',
          title: t('timeRecords:table.columnTitle.day'),
          width: 110,
          sortingFn: (rowA, rowB) =>
            rowA.data.day?.localeCompare(rowB.data.day),
          Cell: ({ originalData: { data } }) => (
            <TableCell classes={classes}>
              {moment(data.day).format('DD.MM.YYYY')}
            </TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'projectId',
          accessor: 'data.projectId',
          title: t('timeRecords:table.columnTitle.projectName'),
          filterObject: {
            id: 'projectId',
            options: projectOptions,
          },
          width: 170,
          sortingFn: (rowA, rowB) =>
            getProjectById(rowA.data.projectId)?.number.localeCompare(
              getProjectById(rowB.data.projectId)?.number
            ),
          Cell: ({ originalData: { data } }) => {
            const project = getProjectById(data.projectId);
            return (
              <TableCell classes={classes}>
                {data.projectId && `${project?.number} ${project?.shortName}`}
              </TableCell>
            );
          },
          alignSelf: true,
        },
        {
          id: 'title',
          accessor: 'data.title',
          title: t('timeRecords:table.columnTitle.title'),
          width: 170,
          sortingFn: (rowA, rowB) =>
            rowA.data.title?.localeCompare(rowB.data.title),
          Cell: ({ originalData: { data } }) => (
            <TableCell classes={classes}>{data.title}</TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'durationInMinutes',
          accessor: 'data.durationInMinutes',
          title: t('timeRecords:table.columnTitle.duration'),

          width: 100,
          sortingFn: (rowA, rowB) =>
            sumUpDurationInMinutes(rowA.data.timeRecordEntries) -
            sumUpDurationInMinutes(rowB.data.timeRecordEntries),
          Cell: ({ originalData }) => (
            <TableCell classes={classes}>
              {formatMinutesFloat(
                sumUpDurationInMinutes(originalData.data.timeRecordEntries) ?? 0
              )}
            </TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'kilometerDistance',
          accessor: 'data.kilometerDistance',
          title: t('timeRecords:table.columnTitle.kilometerDistance'),

          width: 100,
          sortingFn: (rowA, rowB) =>
            rowA.data.kilometerDistance - rowB.data.kilometerDistance,
          Cell: ({ originalData: { data } }) => (
            <TableCell
              classes={classes}
            >{`${data.kilometerDistance} km`}</TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'projectPhase',
          accessor: 'calculated.projectPhase',
          title: t('timeRecords:table.columnTitle.projectPhase'),
          filterObject: {
            id: 'projectPhase',
            options: projectPhaseOptions,
          },
          width: 140,
          sortingFn: (a, b) => {
            const phaseA = a.calculated.projectPhase || '';
            const phaseB = b.calculated.projectPhase || '';

            return phaseA.localeCompare(phaseB);
          },
          Cell: ({ originalData: { calculated } }) => (
            <TableCell classes={classes}>
              {calculated.projectPhase ?? '-'}
            </TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'contactId',
          accessor: 'data.contactId',
          title: t('timeRecords:table.columnTitle.contactName'),
          filterObject: {
            id: 'contactId',
            options: contactOptions,
          },
          width: 160,
          sortingFn: (rowA, rowB) =>
            sortContactsHelper(
              getContactById(rowA.data.contactId),
              getContactById(rowB.data.contactId)
            ),
          Cell: ({ originalData: { data } }) => (
            <TableCell classes={classes}>
              {getContactById(data.contactId)?.firstName +
                ' ' +
                getContactById(data.contactId)?.lastName}
            </TableCell>
          ),
          alignSelf: true,
        },
        {
          id: 'invoiceNumber',
          accessor: 'data.invoiceNumber',
          title: t('timeRecords:table.columnTitle.invoiceNumber'),

          width: 100,
          sortingFn: (a, b) =>
            a.data.invoiceNumber?.localeCompare(b.data.invoiceNumber),
          Cell: ({ originalData: { data } }) => (
            <TableCell classes={classes}>{data.invoiceNumber ?? '-'}</TableCell>
          ),
          alignSelf: true,
        },
      ],
      [
        getContactById,
        t,
        getProjectById,
        projectOptions,
        projectPhaseOptions,
        contactOptions,
        classes,
      ]
    );
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      setTimeRecordSearchResultItems?.(timeRecords);
    }, [timeRecords, setTimeRecordSearchResultItems]);

    useEffect(() => {
      setTimeRecords?.(_timeRecords);
    }, [_timeRecords, setTimeRecords]);

    useImperativeHandle(ref, () => ({
      fetchTimeRecords: () => {
        fetchSearch();
      },
      resetActiveFilters: () => {
        if (innerRef?.current) {
          innerRef.current.setActiveFilters({});
        }
      },
      timeRecords: timeRecords,
    }));
    //#endregion

    return (
      <FilterContextVirtualTable<TimeRecordSearchResultItem>
        id={tableId}
        ref={innerRef}
        className={className}
        data={timeRecords}
        columns={columns}
        onRow={handleOnRowClick}
        onSelectionChange={handleOnSelectionChange}
        onFilterChange={handleOnFilterChange}
        onCheckEquality={(a, b) => a.data.timeRecordId === b.data.timeRecordId}
        resizable="absolute"
        noItemsScreen={<FilterResultNoItemsScreen />}
        loading={
          isLoading && {
            type: 'noItems',
          }
        }
        rowsAreSelectable
        actionBarButtons={actionBarButtons as any}
        actionBarDefaultButtons={
          (filteredTimeRecords ?? timeRecords).length > 0
            ? [
                {
                  children: t('common:actions.csvExport'),
                  iconProp: 'file-arrow-down',
                  onClick: () => {
                    exportToCsv(filteredTimeRecords ?? timeRecords);
                  },
                },
                {
                  children: t('common:actions.excelExport'),
                  iconProp: 'file-excel',
                  onClick: handleOnExcelExportClick,
                },
                ...(showCreateReportButton && isBetaFeaturesEnabled
                  ? [
                      {
                        children: `${t('timeRecords:table.actionBarActions.createReport')} (Beta)`,
                        iconProp: 'file' as IconProp,
                        onClick: createBetaReport,
                      },
                    ]
                  : []),
                ...(showCreateReportButton
                  ? [
                      {
                        children: t(
                          'timeRecords:table.actionBarActions.createReport'
                        ),
                        iconProp: 'file' as IconProp,
                        onClick: createReport,
                      },
                    ]
                  : []),
              ]
            : []
        }
      />
    );
  }
);

export default TimeRecordsPageTable;

interface TableCellProps {
  children?: string;
  classes: Classes<'tableCell'>;
}

export const TableCell: React.FC<TableCellProps> = (props) => {
  //#region ------------------------------ Defaults
  const { children, classes } = props;
  //#endregion

  return (
    <div className={classes.tableCell} title={children}>
      {children}
    </div>
  );
};
